import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import useHttp from '../../hooks/http.hook';
import '../business/Business.css';
import '../orders/OrdersPage.css';
import './ShipmentsPage.css';
import CreateBusinessHeader from '../business/components/CreateBusinessHeader';
import DateRangeCalendarModal, {
  formatDateFilterLabel,
} from '../orders/components/DateRangeCalendarModal';
import OrderFilterSelectModal from '../orders/components/OrderFilterSelectModal';
import {
  buildStatusFilterOptions,
  getProviderStatusLabel,
  sortCustomOrderStatuses,
  statusBadgeClassName,
} from '../../types/OrderStatusTypes';
import { formatPaymentShortDate } from '../orders/components/dateUtils';

const PAGE_SIZE = 20;

function parseStatusIdsParam(value) {
  if (!value) return [];
  return String(value)
    .split(',')
    .map((id) => Number.parseInt(id.trim(), 10))
    .filter((id) => Number.isFinite(id));
}

function parseDateParam(value) {
  const raw = String(value ?? '').trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : null;
}

function applyShipmentFilterParams(prev, updates) {
  const next = new URLSearchParams(prev);
  Object.entries(updates).forEach(([key, value]) => {
    if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) {
      next.delete(key);
      return;
    }
    next.set(key, Array.isArray(value) ? value.join(',') : String(value));
  });
  if (!next.get('dateFrom')) {
    next.delete('dateTo');
  }
  return next;
}

function formatStatusFilterLabel(statusIds, options) {
  if (!statusIds.length) return 'Статусы';
  const labels = (options || [])
    .filter((item) => statusIds.includes(item.id))
    .map((item) => item.label);
  return `Статусы (${labels.join(', ')})`;
}

function formatMoney(value) {
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(n)) return '0.00 сом';
  return `${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} сом`;
}

function displayText(value) {
  const text = String(value ?? '').trim();
  if (!text || text === '-') return '—';
  return text;
}

function getRecordDateKey(record) {
  const raw = record?.date;
  if (!raw) return '';
  const d = typeof raw === 'string' ? new Date(raw) : raw;
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDateLabel(dateKey) {
  if (!dateKey) return dateKey;
  const [y, m, d] = dateKey.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const today = new Date();
  const isToday = date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();
  if (isToday) return 'Сегодня';
  if (isYesterday) return 'Вчера';
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

function groupRecordsByDate(records) {
  const byDate = {};
  records.forEach((record) => {
    const key = getRecordDateKey(record);
    if (!key) return;
    if (!byDate[key]) byDate[key] = [];
    byDate[key].push(record);
  });
  const keys = Object.keys(byDate).sort((a, b) => b.localeCompare(a));
  return keys.map((key) => ({
    dateKey: key,
    dateLabel: formatDateLabel(key),
    records: byDate[key],
  }));
}

function ShipmentRecordsPage() {
  const navigate = useNavigate();
  const { branchId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { requestWithMeta } = useHttp();

  const [items, setItems] = React.useState([]);
  const [page, setPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [calendarOpen, setCalendarOpen] = React.useState(false);
  const [statusModalOpen, setStatusModalOpen] = React.useState(false);
  const [customStatuses, setCustomStatuses] = React.useState([]);

  const selectedStatusKey = searchParams.get('statusIds') || '';
  const selectedStatusIds = React.useMemo(
    () => parseStatusIdsParam(selectedStatusKey),
    [selectedStatusKey]
  );
  const dateFrom = parseDateParam(searchParams.get('dateFrom'));
  const dateTo = parseDateParam(searchParams.get('dateTo')) || dateFrom;

  const contentRef = React.useRef(null);
  const sentinelRef = React.useRef(null);

  const hasMore = page < totalPages;
  const dateGroups = React.useMemo(() => groupRecordsByDate(items), [items]);
  const statusOptions = React.useMemo(
    () => buildStatusFilterOptions(customStatuses),
    [customStatuses]
  );
  const allStatusIds = statusOptions.map(({ id }) => id);
  const dateFilterKey = `${dateFrom || ''}|${dateTo || ''}`;
  const dateFilterSelected = Boolean(dateFrom);
  const statusFilterSelected = selectedStatusIds.length > 0;
  const recordsSearch = searchParams.toString();

  const setFilterParams = React.useCallback((updates) => {
    setSearchParams((prev) => applyShipmentFilterParams(prev, updates), { replace: true });
  }, [setSearchParams]);

  const openOrder = (orderId) => {
    if (!orderId) return;
    navigate(
      {
        pathname: `/branch/${branchId}/orders/${orderId}`,
        search: recordsSearch,
      },
      {
        state: {
          backTo: {
            pathname: `/branch/${branchId}/shipments`,
            search: recordsSearch,
          },
        },
      }
    );
  };

  const fetchPage = React.useCallback(
    async (pageNum) => {
      const params = new URLSearchParams({
        page: String(pageNum),
        limit: String(PAGE_SIZE),
      });
      if (selectedStatusIds.length > 0) {
        params.set('statusIds', selectedStatusIds.join(','));
      }
      if (dateFrom) {
        params.set('dateFrom', dateFrom);
        params.set('dateTo', dateTo || dateFrom);
      }
      const res = await requestWithMeta(
        `/operator/branches/${encodeURIComponent(branchId)}/shipment-records?${params.toString()}`,
        'GET'
      );
      if (!res?.ok) {
        return { ok: false, rows: [], totalPages: 1 };
      }
      const list = Array.isArray(res?.data?.records) ? res.data.records : [];
      const pagination = res?.data?.pagination ?? {};
      return {
        ok: true,
        rows: list,
        totalPages: pagination.totalPages ?? 1,
      };
    },
    [branchId, requestWithMeta, selectedStatusIds, dateFrom, dateTo]
  );

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await requestWithMeta('/operator/custom-order-statuses', 'GET');
        if (cancelled) return;
        const list = Array.isArray(res?.data?.customOrderStatuses)
          ? res.data.customOrderStatuses
          : [];
        setCustomStatuses(sortCustomOrderStatuses(list));
      } catch (_) {
        if (!cancelled) setCustomStatuses([]);
      }
    })();
    return () => { cancelled = true; };
  }, [requestWithMeta]);

  React.useEffect(() => {
    if (!branchId || branchId === 'store') {
      navigate('/branch/store', { replace: true });
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setPage(1);
    setItems([]);
    setTotalPages(1);

    (async () => {
      try {
        const result = await fetchPage(1);
        if (cancelled) return;
        if (!result.ok) {
          setError('Не удалось загрузить доставки');
          setItems([]);
          return;
        }
        setItems(result.rows);
        setTotalPages(result.totalPages);
      } catch (_) {
        if (!cancelled) {
          setError('Ошибка загрузки');
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [branchId, navigate, fetchPage, selectedStatusKey, dateFilterKey]);

  React.useEffect(() => {
    const root = contentRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [e] = entries;
        if (!e?.isIntersecting) return;
        if (loading || loadingMore || page >= totalPages) return;

        setLoadingMore(true);
        fetchPage(page + 1)
          .then((result) => {
            if (!result.ok) return;
            setItems((prev) => [...prev, ...result.rows]);
            setPage((p) => p + 1);
            setTotalPages(result.totalPages);
          })
          .finally(() => setLoadingMore(false));
      },
      { root, rootMargin: '100px', threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchPage, hasMore, loading, loadingMore, page, totalPages]);

  const handleBack = () => {
    navigate(`/branch/${branchId}`);
  };

  const handleApplyDateFilter = ({ start, end }) => {
    setFilterParams({
      dateFrom: start || null,
      dateTo: end || start || null,
    });
  };

  const handleApplyStatusFilter = (ids) => {
    const unique = [...new Set(ids)];
    const isAll =
      unique.length === 0 ||
      (unique.length === allStatusIds.length && allStatusIds.every((id) => unique.includes(id)));
    setFilterParams({ statusIds: isAll ? [] : unique });
  };

  return (
    <div className="orders-page-root">
      <CreateBusinessHeader
        title="Доставка"
        subtitle=""
        onBack={handleBack}
      />

      <main ref={contentRef} className="orders-page-content">
        <div className="orders-page-statusFilters" role="group" aria-label="Фильтр доставок">
          <button
            type="button"
            className={`orders-page-statusFilterTag${dateFilterSelected ? ' is-selected' : ''}`}
            aria-pressed={dateFilterSelected}
            onClick={() => setCalendarOpen(true)}
          >
            {formatDateFilterLabel(dateFrom, dateTo)}
          </button>
          <button
            type="button"
            className={`orders-page-statusFilterTag${statusFilterSelected ? ' is-selected' : ''}`}
            aria-pressed={statusFilterSelected}
            onClick={() => setStatusModalOpen(true)}
          >
            {formatStatusFilterLabel(selectedStatusIds, statusOptions)}
          </button>
        </div>

        {loading ? (
          <div className="orders-page-loading" aria-busy="true">
            <div className="orders-page-skeleton orders-page-skeleton--block" />
            <div className="orders-page-skeleton orders-page-skeleton--block" />
            <div className="orders-page-skeleton orders-page-skeleton--block" />
          </div>
        ) : error ? (
          <div className="orders-page-error">{error}</div>
        ) : dateGroups.length === 0 ? (
          <div className="orders-page-empty">Нет доставок</div>
        ) : (
          <>
            {dateGroups.map((group) => (
              <section key={group.dateKey} className="orders-page-dateSection">
                <h2 className="orders-page-dateHeading">{group.dateLabel}</h2>
                <div className="orders-page-tableWrap">
                  <table
                    className="orders-page-table orders-page-table--payments orders-page-table--shipments"
                    aria-label={`Доставки за ${group.dateLabel}`}
                  >
                    <thead>
                      <tr>
                        <th scope="col">Адрес</th>
                        <th scope="col">Сумма</th>
                        <th scope="col" className="orders-page-colDesktop">Клиент</th>
                        <th scope="col" className="orders-page-colDesktop">Дата</th>
                        <th scope="col">Статус</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.records.map((record) => {
                        const id = record?.id;
                        const orderId = record?.orderId;
                        const statusId = Number(record?.statusId);
                        const address = displayText(record?.address);

                        return (
                          <tr
                            key={id}
                            className="orders-page-row"
                            tabIndex={0}
                            onClick={() => openOrder(orderId)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openOrder(orderId);
                              }
                            }}
                          >
                            <td className="orders-page-itemName">{address}</td>
                            <td className="orders-page-itemId">{formatMoney(record?.price)}</td>
                            <td className="orders-page-colDesktop orders-page-itemCustomer">
                              {displayText(record?.customer)}
                            </td>
                            <td className="orders-page-colDesktop">
                              {formatPaymentShortDate(record?.date)}
                            </td>
                            <td>
                              <span className={statusBadgeClassName('orders-page-itemStatus', statusId)}>
                                {getProviderStatusLabel(statusId, customStatuses)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            ))}
            <div ref={sentinelRef} className="orders-page-sentinel" aria-hidden="true" />
            {loadingMore ? (
              <div className="orders-page-loadingMore" aria-busy="true">
                <div className="orders-page-skeleton orders-page-skeleton--block" />
                <div className="orders-page-skeleton orders-page-skeleton--block" />
              </div>
            ) : null}
          </>
        )}
      </main>

      <footer className="orders-page-footerRow">
        <button
          type="button"
          className="orders-page-footerButton orders-page-footerButtonPrimary"
          onClick={() => navigate(`/branch/${branchId}/shipments/plans`)}
        >
          Планы доставки
        </button>
      </footer>

      <DateRangeCalendarModal
        isOpen={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        initialStart={dateFrom}
        initialEnd={dateTo}
        onApply={handleApplyDateFilter}
      />
      <OrderFilterSelectModal
        isOpen={statusModalOpen}
        title="Статусы"
        options={statusOptions}
        selectedIds={selectedStatusIds}
        multiple
        resetIds={allStatusIds}
        onApply={handleApplyStatusFilter}
        onClose={() => setStatusModalOpen(false)}
      />
    </div>
  );
}

export default ShipmentRecordsPage;
